import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';

import { Fragment, SyntheticEvent } from 'react';

import { Radius, Spacing, Typography } from 'chatie-design-system';

import Badge from '@components/common/Badge';
import EFinderImage from '@components/common/EFinderImage';
import RoundAvatar from '@components/common/RoundAvatar';
import SwipeableListContainer from '@components/common/SwipableListContainer';

import { IContentByProfileFromGallery } from '@base/outlookModel';
import { Platform } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { multilineEllipsis } from '@styles/css';
import { addDuration, isBefore } from '@utils/date';
import { useWebView } from '@utils/webview';

import OutlookButton from './OutlookButton';

const groupCurationCSS = (theme: Theme) => css`
  display: flex-col;
  padding: ${Spacing.small}px 0;

  h5 {
    ${Typography.h5};
    line-height: 100%;
    margin-bottom: ${Spacing.xxsmall}px;
    padding-left: ${Spacing.medium}px;
  }

  .chat-room-container {
    padding: ${Spacing.small}px 26px !important;
  }
  .chat-room-header {
    margin-left: ${Spacing.small}px;
  }

  color: ${theme.var.gray900};
`;

const listContainerCSS = css`
  display: flex;
  margin-top: ${Spacing.xsmall}px;
  gap: ${Spacing.small}px;
  padding: 0 ${Spacing.medium}px;
  .situationListContainer {
  }
`;

const situationContainerCSS = (
  style:
    | {
        width: number;
        height: number;
        border_radius: number;
        image_background_color: string;
      }
    | undefined
) => css`
  display: flex;
  flex-direction: column;
  justify-content: initial;
  width: ${style?.width ? `${style?.width}px` : `100px`} !important;
  align-items: center;
  gap: ${Spacing.xxsmall}px;
`;
const situationInfoCSS = css`
  display: flex;
  flex-direction: column;
  width: 100%;
  ${multilineEllipsis(1)};
  > p {
    padding: 0 ${Spacing.xxsmall}px;
    ${Typography.body12};
    line-height: 145%;
    display: -webkit-box;
    word-wrap: break-all;
    overflow: hidden;
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    ${multilineEllipsis(1)};
  }
`;

const situationImgCSS = (
  style:
    | {
        width: number;
        height: number;
        border_radius: number;
        image_background_color: string;
      }
    | undefined
) => css`
  height: ${style?.height ? `${style?.height}px` : `100px`} !important;
  width: ${style?.width ? `${style?.width}px` : `100px`} !important;
  background-color: ${style?.image_background_color
    ? style?.image_background_color
    : null};
  top: 0;
  position: relative;
  border-radius: ${style?.border_radius ? `${style?.border_radius}px` : ''};
  overflow: hidden;

  .new-badge {
    ${Typography.body11};
    display: flex;
    align-items: center;
    height: 16px;
    position: absolute;
    z-index: 1;
    top: ${Spacing.xxsmall}px;
    left: ${Spacing.xxsmall}px;
  }
`;

const profileCSS = css`
  display: flex;
  justify-content: flex-start;
  padding: 0 4px;
  height: 16px;
  margin-top: ${Spacing.xxsmall}px;

  p {
    ${Typography.body12};
    margin-left: ${Spacing.xhsmall}px;
  }
`;

const headerCSS = css`
  display: flex;
  justify-content: space-between;
  padding-right: ${Spacing.medium}px;
  margin-bottom: ${Spacing.xhsmall}px;
`;

export interface ChatSituationGroupCurationnProps {
  data: IContentByProfileFromGallery;
  onClickGround: (id: number, groundId: number) => void;
  onClickSituation: ((situationId: number) => void) | undefined;
  onClickProfile: ((id: number) => void) | undefined;
  order: string | number;
}

const ContentByProfileFromGallery = ({
  data,
  onClickGround,
  onClickProfile,
  onClickSituation,
  order,
}: ChatSituationGroupCurationnProps) => {
  const { t } = useTranslation();
  const { title, contents, button, content_style } = data;
  const type = data?.content_object_type;
  const isPaygroundType = type === 'playground';
  const presetKey = `order:${order}-ContentByProfileFromGallery`;
  const { web } = useWebView();
  const { platform, isCheming } = useGlobalContext();
  const router = useRouter();

  const handleClickOutlookButton = () => {
    if (button.url && button.url !== '') {
      if (platform === Platform.web) {
        router.push(button.url);
      }
      web.openPage(button.url);
    }
  };

  return (
    <div css={groupCurationCSS} key={presetKey}>
      <div css={headerCSS}>
        <h5>{t(title)}</h5>
        {button && (
          <OutlookButton
            type={button?.type}
            text={button?.text}
            onClickButton={handleClickOutlookButton}
          />
        )}
      </div>
      <SwipeableListContainer className="situationListContainer">
        <div css={listContainerCSS}>
          {contents?.map((content, idx) => {
            const hasCover = !!content?.cover_image?.key;
            const image = hasCover ? content?.cover_image : content?.image;
            if (!image?.key) {
              return;
            }
            const isContain =
              data?.content_object_type === 'playground' &&
              image?.attachment?.width &&
              image?.attachment?.height &&
              image?.attachment?.width > image?.attachment?.height;
            const isNewSituation =
              (content?.payload?.released_at &&
                isBefore(
                  new Date(),
                  addDuration(new Date(content?.payload?.released_at), {
                    days: 3,
                  })
                )) ||
              false;

            const handleClick = () => {
              if (isCheming && onClickProfile) {
                onClickProfile(content.profile.id);
              } else if (isPaygroundType) {
                onClickGround(content.profile.id, content.id);
              } else {
                if (onClickSituation) {
                  onClickSituation(content.id);
                }
              }
            };

            const handleClickProfile = (e: SyntheticEvent) => {
              e.stopPropagation();
              if (!isPaygroundType && onClickProfile) {
                onClickProfile(content.profile.id);
              }
            };

            return (
              <Fragment key={presetKey + `idx:${idx}`}>
                <div
                  css={situationContainerCSS(content_style)}
                  key={content.id}
                  onClick={handleClick}
                >
                  <div css={situationImgCSS(content_style)}>
                    {isNewSituation && (
                      <Badge
                        text={'NEW'}
                        className={'new-badge'}
                        backgroundColor={
                          isCheming
                            ? 'linear-gradient(180deg, #7F00FF, #8E2DE2)'
                            : 'linear-gradient(90deg, #04C8D0, #1590D5)'
                        }
                        radius={Radius.xsmall}
                        fontSize={10}
                      />
                    )}
                    <EFinderImage
                      image={image}
                      width={data?.content_style?.width ?? 100}
                      height={data?.content_style?.height ?? 100}
                      radius={data?.content_style?.border_radius ?? 5}
                      layout="fill"
                      objectFit={isContain ? 'contain' : 'cover'}
                    />
                  </div>
                  <div css={situationInfoCSS}>
                    <p className="multiline">{t(content.title)}</p>

                    <div css={profileCSS} onClick={handleClickProfile}>
                      <RoundAvatar size={14} picture={content.profile?.image} />
                      <p>{t(content.profile.title)}</p>
                    </div>
                  </div>
                </div>
              </Fragment>
            );
          })}
        </div>
      </SwipeableListContainer>
    </div>
  );
};

export default ContentByProfileFromGallery;
